import { useEffect, useMemo, useState } from "react";
import api, { fmtSeconds, fmtClockTime } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { CalendarDays, Sparkles, AlertTriangle, LogIn, LogOut } from "lucide-react";

const RANGES = [
  { key: "day", label: "Day", days: 1 },
  { key: "week", label: "Week", days: 7 },
  { key: "month", label: "Month", days: 30 },
];

export default function EmployeeHistory() {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState("week");
  const days = RANGES.find((r) => r.key === range)?.days || 7;

  useEffect(() => {
    api.get("/tracker/history", { params: { days: 60 } }).then((r) => setRows(r.data));
  }, []);

  // Filter rows for the selected range (most recent N days, including today)
  const filtered = useMemo(() => {
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - (days - 1));
    return rows.filter((r) => r.date >= cutoff.toISOString().slice(0, 10));
  }, [rows, days]);

  // Aggregate KPIs for the range
  const k = useMemo(() => {
    const t = { productive: 0, idle: 0, light: 0, total: 0, keystrokes: 0, clicks: 0, moves: 0, scrolls: 0, sessions: 0 };
    filtered.forEach((r) => {
      t.productive += r.productive_seconds || 0;
      t.idle += r.idle_seconds || 0;
      t.light += r.light_seconds || 0;
      t.total += r.total_seconds || 0;
      t.keystrokes += r.keystrokes || 0;
      t.clicks += r.mouse_clicks || 0;
      t.moves += r.mouse_moves || 0;
      t.scrolls += r.scrolls || 0;
      t.sessions += 1;
    });
    t.engaged = Math.max(0, t.productive - t.light);
    t.engagedRatio = t.productive > 0 ? Math.round((t.engaged / t.productive) * 100) : 0;
    t.lightRatio = t.productive > 0 ? Math.round((t.light / t.productive) * 100) : 0;
    return t;
  }, [filtered]);

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ History ]</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-2">Work History</h1>
        <p className="font-display-italic text-sm text-stone-500 mb-6">
          Your hours, productivity breakdown, and input activity — by day, week, or month.
        </p>

        {/* Range tabs */}
        <div className="inline-flex border border-stone-200 rounded-md overflow-hidden mb-5 bg-white">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`history-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-bold transition-colors ${
                range === r.key
                  ? "bg-[#1f4d2e] text-white"
                  : "bg-white text-stone-700 hover:bg-stone-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-6">
          {[
            { l: "Productive", v: fmtSeconds(k.productive), tag: `${(k.productive/3600).toFixed(2)} h`, color: "text-[#1f4d2e]" },
            { l: "Engaged", v: fmtSeconds(k.engaged), tag: `${k.engagedRatio}% of productive`, color: "text-[#1f4d2e]" },
            { l: "Light (no clicks/keys)", v: fmtSeconds(k.light), tag: `${k.lightRatio}% of productive`, color: k.lightRatio > 30 ? "text-[#a8421f]" : "text-[#7a5e0f]" },
            { l: "Idle", v: fmtSeconds(k.idle), tag: `${k.sessions} day-sessions`, color: "text-stone-500" },
          ].map((kpi) => (
            <div key={kpi.l} className="bg-white p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 mb-1.5">{kpi.l}</div>
              <div className={`font-mono-num text-3xl font-semibold tracking-tight ${kpi.color}`}>{kpi.v}</div>
              <div className="font-mono-num text-xs text-stone-500 mt-1">{kpi.tag}</div>
            </div>
          ))}
        </div>

        {/* Insight banner */}
        {k.productive > 60 && k.lightRatio > 30 && (
          <div className="mb-6 p-4 border border-[#a8421f]/40 bg-[#a8421f]/5 rounded-md text-sm text-[#a8421f] flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <span className="font-bold">{k.lightRatio}% of your productive time</span> for this {range} was mouse-movement-only —
              no keystrokes or clicks. If this is unintentional, take a break and resume real work; admins can see this too.
            </div>
          </div>
        )}
        {k.productive > 60 && k.engagedRatio >= 70 && (
          <div className="mb-6 p-4 border border-[#1f4d2e]/30 bg-[#1f4d2e]/5 rounded-md text-sm text-[#1f4d2e] flex items-start gap-2.5">
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <span className="font-bold">Great focus</span> — {k.engagedRatio}% of your productive time this {range} had real keyboard & click activity.
            </div>
          </div>
        )}

        {/* Per-input counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-6">
          {[
            { l: "Keystrokes", v: k.keystrokes },
            { l: "Clicks", v: k.clicks },
            { l: "Mouse moves", v: k.moves },
            { l: "Scrolls", v: k.scrolls },
          ].map((x) => (
            <div key={x.l} className="bg-white p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">{x.l}</div>
              <div className="font-mono-num text-2xl font-semibold mt-1">{x.v.toLocaleString()}</div>
            </div>
          ))}
        </div>

        {/* Per-day breakdown table */}
        <div className="border border-stone-200">
          <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-stone-500" />
            <div className="text-xs tracking-[0.25em] uppercase font-bold">Daily Breakdown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-white border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Date</th>
                  <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogIn className="h-3 w-3" /> Time In</span></th>
                  <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogOut className="h-3 w-3" /> Time Out</span></th>
                  <th className="text-right px-5 py-3 font-bold">Productive</th>
                  <th className="text-right px-5 py-3 font-bold">Engaged</th>
                  <th className="text-right px-5 py-3 font-bold">Light</th>
                  <th className="text-right px-5 py-3 font-bold">Idle</th>
                  <th className="text-right px-5 py-3 font-bold">Total</th>
                  <th className="text-right px-5 py-3 font-bold">Quality</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((h) => {
                  const lightSec = h.light_seconds || 0;
                  const prod = h.productive_seconds || 0;
                  const eng = Math.max(0, prod - lightSec);
                  const lightR = prod > 0 ? Math.round((lightSec / prod) * 100) : 0;
                  return (
                    <tr key={h.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-5 py-3 font-mono-num font-bold">{h.date}</td>
                      <td className="px-5 py-3 font-mono-num text-[#1f4d2e]">{fmtClockTime(h.first_start_at)}</td>
                      <td className="px-5 py-3 font-mono-num text-[#a8421f]">
                        {h.is_running ? <span className="text-stone-400">running…</span> : fmtClockTime(h.last_stop_at)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-num text-[#1f4d2e] font-bold">{fmtSeconds(prod)}</td>
                      <td className="px-5 py-3 text-right font-mono-num text-[#1f4d2e]">{fmtSeconds(eng)}</td>
                      <td className={`px-5 py-3 text-right font-mono-num ${lightR > 30 ? "text-[#a8421f]" : "text-[#7a5e0f]"}`}>{fmtSeconds(lightSec)}</td>
                      <td className="px-5 py-3 text-right font-mono-num text-stone-500">{fmtSeconds(h.idle_seconds || 0)}</td>
                      <td className="px-5 py-3 text-right font-mono-num font-bold">{fmtSeconds(h.total_seconds || 0)}</td>
                      <td className="px-5 py-3 text-right">
                        {prod > 0 ? (
                          <div className="inline-flex items-center gap-2 justify-end">
                            <div className="w-20 h-1.5 bg-stone-100 rounded-full overflow-hidden flex">
                              <div className="h-full bg-[#1f4d2e]" style={{ width: `${100 - lightR}%` }} />
                              <div className="h-full bg-[#d4a82a]" style={{ width: `${lightR}%` }} />
                            </div>
                            <span className="text-xs font-mono-num text-stone-500 w-9 text-right">{lightR}%L</span>
                          </div>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan="9" className="text-center py-12 text-sm text-stone-500">No sessions in the selected range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
